/**
 * tenantStore — Plan E E.5+E.6
 *
 * Single source of truth for the current tenant identity in the SPA.
 *
 * Why a dedicated store (D.G3 reviewer concern, made atomic in E.5+E.6):
 *   `projectStore.currentProject?.tenant` was the previous source. That field
 *   was driven by a hardcoded TopBar dropdown (`['acme-corp', 'demo-tenant',
 *   'gamemagick']`) inherited from the pre-Plan-D legacy admin. A stale
 *   project selection would silently target wrong-tenant ttyd endpoints.
 *
 * Contract:
 *   - Fetch once on App mount via `fetchTenant()` → GET /api/me
 *   - All terminal URL construction reads `tenantId` from this store
 *   - On error, `tenantId` stays null and consumers render an empty state
 *
 * Auth: post-A.10 (2026-04-26/27), vibe-designer is Mode 1 OIDC PKCE — the
 * SPA owns auth and the backend's AuthMiddleware enforces JWT on every
 * non-public route. `/api/me` requires `Authorization: Bearer <token>`.
 * This store routes through `services/api.ts` `api.get()`, which pulls the
 * token from `localStorage['vibe_designer_token']` automatically.
 */
import { create } from 'zustand';
import { api } from '../services/api';

/** 1.108.0 Phase 3a — per-tenant MCP availability map returned by /api/me.
 * Each key gates the corresponding SOURCE toggle in ChatPanelPi's chrome
 * sub-row. Capability=false renders the toggle disabled-with-tooltip.
 *
 * Phase 3a backend wires only `mind` to a live probe; the other 5 are
 * hardcoded `false` until Phase 3b+ ships the corresponding integration
 * modules. SPA renders all 6 keys conditionally: present-and-true = enabled,
 * present-and-false = disabled, missing = treated as false (defensive). */
export type McpCapabilities = Partial<Record<
  'mind' | 'cortex' | 'brain' | 'sight' | 'cognition' | 'obsidian' | 'web_search',
  boolean
>>;

interface TenantState {
  tenantId: string | null;
  /** 1.108.0: per-tenant MCP capability map. `null` until /api/me returns
   * (initial mount + 1.106.0 backend backward-compat). Render disabled-tooltip
   * for any key not explicitly `true`. */
  mcpCapabilities: McpCapabilities | null;
  isLoading: boolean;
  error: Error | null;
  fetchTenant: () => Promise<void>;
  reset: () => void;
}

export const useTenantStore = create<TenantState>((set, get) => ({
  tenantId: null,
  mcpCapabilities: null,
  isLoading: false,
  error: null,

  fetchTenant: async () => {
    // E.G3 reviewer C1: idempotency guard. React StrictMode double-invokes
    // effects in dev, and any future component mounting may also call
    // fetchTenant(). A second concurrent fetch is benign (same TENANT_ID
    // returned), but the guard makes the "fetch once" contract explicit.
    if (get().tenantId !== null || get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<{
        tenant_id?: string;
        mcp_capabilities?: McpCapabilities;
      }>('/me');
      set({
        tenantId: data.tenant_id ?? null,
        mcpCapabilities: data.mcp_capabilities ?? null,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      set({ tenantId: null, mcpCapabilities: null, isLoading: false, error: err as Error });
    }
  },

  reset: () => set({ tenantId: null, mcpCapabilities: null, isLoading: false, error: null }),
}));
